/**
 * useListCardGroups — grouping + within-group sorting for the list detail views.
 *
 * `priority` is a list-only grouping handled here; the shared modes delegate to `groupCards`.
 */
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { getTypeSortOrder, groupCards, groupCardsByBucket, type CardGroup } from '@/lib/cardGrouping';
import { getCmc } from '@/lib/cardUtils';
import { fullCardCacheAtom } from '@/state/cardCache';
import { cardClassificationByOracleIdAtom } from '@/state/cardClassification';
import type { TypeCategory } from '@/types/card';
import { PRIORITY_LABELS, type EnrichedListCard, type ListPriority } from '@/types/lists';

import { createListCardComparator } from '../lib/listSorting';
import type { ListGroupBy, ListSortState } from '../state/listViewSettings';

const PRIORITY_SORT_ORDER: Record<ListPriority, number> = { high: 0, medium: 1, low: 2 };

export default function useListCardGroups(
  cards: EnrichedListCard[],
  groupBy: ListGroupBy,
  sort: ListSortState,
): CardGroup<EnrichedListCard>[] {
  const fullCardCache = useAtomValue(fullCardCacheAtom);
  const classifications = useAtomValue(cardClassificationByOracleIdAtom);

  return useMemo(() => {
    // Rows whose full card is not cached yet fall back to 'other', which sorts last.
    const typeCategoryOf = (card: EnrichedListCard): TypeCategory => classifications.get(card.oracle_id)?.type_category ?? 'other';
    const compareWithinGroup = createListCardComparator(sort, { typeSortOrder: (card) => getTypeSortOrder(typeCategoryOf(card)) });

    if (groupBy === 'priority') {
      return groupCardsByBucket(
        cards,
        (card) => ({
          key: card.priority,
          label: PRIORITY_LABELS[card.priority],
          sortOrder: PRIORITY_SORT_ORDER[card.priority],
          quantity: card.quantity,
        }),
        compareWithinGroup,
      );
    }

    return groupCards(
      cards,
      groupBy,
      (card) => {
        const fullCard = fullCardCache.get(card.scryfall_id);
        return {
          typeCategory: typeCategoryOf(card),
          cmc: fullCard ? (getCmc(fullCard) ?? 0) : 0,
          colorIdentity: fullCard?.color_identity ?? [],
          quantity: card.quantity,
        };
      },
      compareWithinGroup,
    );
  }, [cards, groupBy, sort, fullCardCache, classifications]);
}
